/** Gap kept between an anchored panel and the viewport edges (Figma 1040:48275) */
export const ANCHORED_PANEL_VIEWPORT_MARGIN = 16
/** Keeps the arrow off the panel's rounded corners */
const ARROW_EDGE_INSET = 24
const PANEL_MAX_WIDTH = 320
/** Below this the panel goes full available width, per the mobile design ($mobile-breakpoint) */
const MOBILE_BREAKPOINT = 600

export interface IAnchoredPanelLayout {
  /** Panel width, shrunk on viewports narrower than the max width + margins */
  width: number
  /** Absolute left of the panel, clamped inside the viewport */
  left: number
  /** Arrow left within the panel, pointing at the target's center */
  arrowLeft: number
}

export const getAnchoredPanelLayout = (
  targetRect: Pick<DOMRect, 'left' | 'width'>,
  viewportWidth: number,
): IAnchoredPanelLayout => {
  const availableWidth = viewportWidth - ANCHORED_PANEL_VIEWPORT_MARGIN * 2
  const width = viewportWidth < MOBILE_BREAKPOINT
    ? availableWidth
    : Math.min(PANEL_MAX_WIDTH, availableWidth)
  const targetCenterX = targetRect.left + targetRect.width / 2
  const maxLeft = viewportWidth - ANCHORED_PANEL_VIEWPORT_MARGIN - width
  const left = Math.max(ANCHORED_PANEL_VIEWPORT_MARGIN, Math.min(targetCenterX - width / 2, maxLeft))
  const arrowLeft = Math.max(ARROW_EDGE_INSET, Math.min(targetCenterX - left, width - ARROW_EDGE_INSET))

  return { width, left, arrowLeft }
}

export const findVisibleTarget = (targetId: string): HTMLElement | null => {
  // `targetId` is remote cinematics config; escape it so it can't widen the
  // selector or throw a SyntaxError inside the timer/scroll handlers.
  try {
    const candidates = document.querySelectorAll<HTMLElement>(`[id="${CSS.escape(targetId)}"]`)

    // The navbar keeps its desktop and mobile variants mounted at once, so the
    // id is deliberately duplicated and only the laid-out one can be anchored.
    for (const candidate of candidates) {
      if (candidate.offsetParent !== null) return candidate
    }
  } catch {
    return null
  }

  return null
}

/**
 * Whether the cutout should be drawn as a circle rather than a rounded box.
 *
 * Squareness is half the test: a pill — `border-radius: 9999px` on something
 * wider than tall — clears the radius check but is not round, and a 50% cutout
 * drew an ellipse whose ends cut through the target it was meant to frame.
 */
export const isRoundTarget = (element: HTMLElement): boolean => {
  const { borderRadius } = window.getComputedStyle(element)
  if (borderRadius.includes('%')) return true
  const isSquare = Math.abs(element.offsetWidth - element.offsetHeight) <= 1
  return isSquare && parseFloat(borderRadius) >= element.offsetHeight / 2
}
